import { cn, cva, IconSize, NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { ButtonHTMLAttributes, ReactNode } from 'react';

type NoddiSelectionCardSize = 'small' | 'medium';
/** `row` (default): icon left, radio right, one card per line. `column`: icon on top, cards side by side. */
type NoddiSelectionCardLayout = 'column' | 'row';

export type NoddiSelectionCardOption<T = string> = {
  value: T;
  label: string;
  description?: string;
  /** Badges under the label, e.g. sales-item tags. */
  badges?: ReactNode;
  /** A badge in the card's top-right corner, beside the radio, e.g. "Suggested". */
  cornerBadge?: ReactNode;
  /** Shown but not selectable, e.g. a choice that is coming soon. */
  disabled?: boolean;
  price?: ReactNode;
  /** Tint for the icon tile, e.g. the season badge colors. */
  iconWrapClassName?: string;
} & ({ icon: NoddiIconName; emoji?: never } | { emoji: string; icon?: never });

// Per-size styling. `medium` is the original card; `small` trims the icon,
// padding, radius, indicator and text for denser in-form use (mirrors NoddiCard's
// `size='small'`). `isSelected` toggles the selected/unselected treatment.
const cardVariants = cva(
  cn(
    'group relative flex border-2 text-left transition-all duration-200',
    'hover:border-primary/50 hover:bg-secondary/50 focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary focus-visible:outline-hidden'
  ),
  {
    variants: {
      size: { medium: 'gap-4 rounded-xl p-5', small: 'gap-3 rounded-lg p-4' },
      layout: { column: 'flex-col items-start', row: 'flex-row items-center' },
      isSelected: {
        true: 'border-primary bg-primary/5',
        false: 'border-muted-foreground/20 bg-card'
      }
    },
    defaultVariants: { size: 'medium', layout: 'row', isSelected: false }
  }
);

const iconWrapVariants = cva('flex shrink-0 items-center justify-center transition-colors', {
  variants: {
    size: { medium: 'size-12 rounded-lg', small: 'size-9 rounded-md' },
    isSelected: {
      true: 'bg-primary text-primary-foreground',
      false: 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
    }
  },
  defaultVariants: { size: 'medium', isSelected: false }
});

const labelVariants = cva('text-foreground', {
  variants: { size: { medium: 'font-semibold', small: 'text-sm font-semibold' } },
  defaultVariants: { size: 'medium' }
});

const descriptionVariants = cva('text-muted-foreground', {
  variants: { size: { medium: 'mt-1 text-sm', small: 'mt-0.5 text-xs' } },
  defaultVariants: { size: 'medium' }
});

// The corner badge and radio. `top`/`right` only take effect in `column`, where they sit in the corner; in `row` they end the line.
const cornerVariants = cva('flex shrink-0 items-center gap-2', {
  variants: {
    size: { medium: 'top-4 right-4', small: 'top-3 right-3' },
    layout: { column: 'absolute', row: '' }
  },
  defaultVariants: { size: 'medium', layout: 'row' }
});

const indicatorVariants = cva('flex shrink-0 items-center justify-center rounded-full border-2 transition-all', {
  variants: {
    size: { medium: 'size-5', small: 'size-4' },
    isSelected: { true: 'border-primary bg-primary', false: 'border-muted-foreground/20' }
  },
  defaultVariants: { size: 'medium', isSelected: false }
});

const indicatorDotVariants = cva('rounded-full bg-primary-foreground', {
  variants: { size: { medium: 'size-2', small: 'size-1.5' } },
  defaultVariants: { size: 'medium' }
});

const iconSizeBySize: Record<NoddiSelectionCardSize, IconSize> = {
  medium: 'large',
  small: 'medium'
};

interface NoddiSelectionCardProps<T = string> extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'onSelect'
> {
  option: NoddiSelectionCardOption<T>;
  isSelected: boolean;
  onSelect: (value: T) => void;
  size?: NoddiSelectionCardSize;
  layout?: NoddiSelectionCardLayout;
}

export const NoddiSelectionCard = <T extends string | number = string>({
  option,
  isSelected,
  onSelect,
  size = 'medium',
  layout = 'row',
  className,
  ...props
}: NoddiSelectionCardProps<T>) => (
  <button
    type='button'
    onClick={() => onSelect(option.value)}
    disabled={option.disabled}
    className={cn(
      cardVariants({ size, layout, isSelected }),
      option.disabled && 'pointer-events-none opacity-60',
      className
    )}
    aria-pressed={isSelected}
    {...props}
  >
    {/* Icon */}
    <div className={cn(iconWrapVariants({ size, isSelected }), option.iconWrapClassName)}>
      {option.icon ? (
        <NoddiIcon name={option.icon} size={iconSizeBySize[size]} />
      ) : (
        <span className={cn('leading-none', size === 'small' ? 'text-lg' : 'text-2xl')}>{option.emoji}</span>
      )}
    </div>

    {/* Content */}
    <div className='min-w-0 flex-1'>
      <h3 className={labelVariants({ size })}>{option.label}</h3>
      {option.badges != null && <div className='mt-1 flex flex-wrap gap-1'>{option.badges}</div>}
      {option.price != null && <div className={descriptionVariants({ size })}>{option.price}</div>}
      {option.description && <p className={descriptionVariants({ size })}>{option.description}</p>}
    </div>

    {/* Corner badge + selection indicator */}
    <div className={cornerVariants({ size, layout })}>
      {option.cornerBadge}
      <div className={indicatorVariants({ size, isSelected })}>
        {isSelected && <div className={indicatorDotVariants({ size })} />}
      </div>
    </div>
  </button>
);

interface NoddiSelectionCardGroupProps<T = string> {
  title?: string;
  description?: string;
  options: NoddiSelectionCardOption<T>[];
  value: T | undefined;
  onValueChange: (value: T) => void;
  size?: NoddiSelectionCardSize;
  /** `column` only for short labels in a known count of options; three side by side wrap unevenly. */
  layout?: NoddiSelectionCardLayout;
  className?: string;
  gridClassName?: string;
}

export const NoddiSelectionCardGroup = <T extends string | number = string>({
  title,
  description,
  options,
  value,
  onValueChange,
  size = 'medium',
  layout = 'row',
  className,
  gridClassName
}: NoddiSelectionCardGroupProps<T>) => {
  const isSmall = size === 'small';
  return (
    // eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-y-3/space-y-5 stack, equals flex flex-col gap-3/gap-5
    <div className={cn(isSmall ? 'space-y-3' : 'space-y-5', className)}>
      {(title || description) && (
        <div>
          {title && <h2 className={cn('font-semibold text-foreground', isSmall ? 'text-base' : 'text-xl')}>{title}</h2>}
          {description && (
            <p className={cn('text-muted-foreground', isSmall ? 'mt-0.5 text-xs' : 'mt-1 text-sm')}>{description}</p>
          )}
        </div>
      )}

      <div className={cn('grid', layout === 'column' && 'sm:grid-cols-2', isSmall ? 'gap-3' : 'gap-4', gridClassName)}>
        {options.map((option) => (
          <NoddiSelectionCard
            key={String(option.value)}
            option={option}
            isSelected={value === option.value}
            onSelect={onValueChange}
            size={size}
            layout={layout}
          />
        ))}
      </div>
    </div>
  );
};
